import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useMarket } from '@contexts/MarketContext';
import { useNotifications } from '@contexts/NotificationContext';
import { Button } from '@components/ui/button';

const notificationStyles: Record<string, { bg: string; icon: string }> = {
  warning: { bg: 'bg-amber-50 text-amber-600', icon: 'text-amber-500' },
  success: { bg: 'bg-emerald-50 text-emerald-600', icon: 'text-emerald-500' },
  info: { bg: 'bg-teal-50 text-teal-600', icon: 'text-teal-500' },
  error: { bg: 'bg-red-50 text-red-600', icon: 'text-red-500' },
};

/**
 * Generate breadcrumbs from current path
 */
function useBreadcrumbs(isBrazil: boolean): Array<{ label: string; path: string }> {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  const breadcrumbs: Array<{ label: string; path: string }> = [];
  let currentPath = '';

  const labelMap: Record<string, string> = isBrazil
    ? {
        dashboard: 'Painel',
        'accounts-payable': 'Contas a Pagar',
        'accounts-receivable': 'Contas a Receber',
        bills: 'Faturas',
        vendors: 'Fornecedores',
        invoices: 'Notas Fiscais',
        customers: 'Clientes',
        new: 'Nova',
        profile: 'Perfil',
        settings: 'Configurações',
        help: 'Ajuda e Suporte',
        notifications: 'Notificações',
      }
    : {
        dashboard: 'Dashboard',
        'accounts-payable': 'Accounts Payable',
        'accounts-receivable': 'Accounts Receivable',
        bills: 'Bills',
        vendors: 'Vendors',
        invoices: 'Invoices',
        customers: 'Customers',
        new: 'New',
        profile: 'Profile',
        settings: 'Settings',
        help: 'Help & Support',
        notifications: 'Notifications',
      };

  for (const segment of pathSegments) {
    currentPath += `/${segment}`;
    breadcrumbs.push({
      label: labelMap[segment] ?? segment.charAt(0).toUpperCase() + segment.slice(1),
      path: currentPath,
    });
  }

  return breadcrumbs;
}

/**
 * Get page title from current path
 */
function usePageTitle(isBrazil: boolean): string {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  const titleMap: Record<string, string> = isBrazil
    ? {
        dashboard: 'Painel',
        bills: 'Faturas',
        vendors: 'Fornecedores',
        invoices: 'Notas Fiscais',
        customers: 'Clientes',
        new: 'Criar Nova',
        profile: 'Perfil',
        settings: 'Configurações',
        help: 'Ajuda e Suporte',
        notifications: 'Notificações',
      }
    : {
        dashboard: 'Dashboard',
        bills: 'Bills',
        vendors: 'Vendors',
        invoices: 'Invoices',
        customers: 'Customers',
        new: 'Create New',
        profile: 'Profile',
        settings: 'Settings',
        help: 'Help & Support',
        notifications: 'Notifications',
      };

  const lastSegment = pathSegments[pathSegments.length - 1];
  return titleMap[lastSegment ?? ''] ?? (isBrazil ? 'Painel' : 'Dashboard');
}

interface HeaderProps {
  onMobileMenuToggle?: () => void;
}

/**
 * Header Component
 * Top header with page title, breadcrumbs, and user info
 * Mobile-optimized with hamburger menu
 */
export function Header({ onMobileMenuToggle }: HeaderProps): JSX.Element {
  const navigate = useNavigate();
  const { user, logout, isLoading } = useAuth();
  const { market, setMarket, config, isBrazil } = useMarket();
  const { notifications, unreadCount, markAsRead, getLocalizedNotification } = useNotifications();
  const breadcrumbs = useBreadcrumbs(isBrazil);
  const pageTitle = usePageTitle(isBrazil);

  // Get unread notifications for the dropdown
  const unreadNotifications = notifications.filter((n) => !n.read);

  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showMarketSelector, setShowMarketSelector] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const marketRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowProfile(false);
      }
      if (marketRef.current && !marketRef.current.contains(event.target as Node)) {
        setShowMarketSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async (): Promise<void> => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="sticky top-0 z-30 bg-white/95 shadow-sm backdrop-blur">
      <div className="flex h-14 sm:h-16 items-center justify-between px-3 sm:px-6">
        {/* Left: Mobile hamburger + Title and Breadcrumbs */}
        <div className="flex items-center gap-3">
          {/* Mobile hamburger menu button */}
          {onMobileMenuToggle && (
            <button
              onClick={onMobileMenuToggle}
              className="flex h-11 w-11 items-center justify-center rounded-xl text-gray-500 hover:bg-teal-50 hover:text-teal-600 transition-colors md:hidden"
              aria-label={isBrazil ? 'Abrir menu' : 'Open menu'}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          <div className="flex flex-col">
            <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate max-w-[150px] sm:max-w-none">{pageTitle}</h1>
            {/* Only show breadcrumbs on desktop when there are 2+ segments */}
            {breadcrumbs.length > 1 && (
              <nav className="hidden sm:flex items-center text-sm text-gray-400" aria-label="Breadcrumb">
                {breadcrumbs.map((crumb, index) => (
                  <span key={crumb.path} className="flex items-center">
                    {index > 0 && (
                      <svg
                        className="mx-2 h-3.5 w-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                    {index === breadcrumbs.length - 1 ? (
                      <span className="font-medium text-teal-600">{crumb.label}</span>
                    ) : (
                      <Link to={crumb.path} className="hover:text-teal-600 transition-colors">
                        {crumb.label}
                      </Link>
                    )}
                  </span>
                ))}
              </nav>
            )}
          </div>
        </div>

        {/* Right: User Info and Actions */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Notifications Bell with Dropdown */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={() => {
                setShowNotifications(!showNotifications);
                setShowProfile(false);
              }}
              className="relative flex h-11 w-11 items-center justify-center rounded-xl text-gray-400 hover:bg-teal-50 hover:text-teal-600 transition-colors"
              aria-label="Notifications"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Notification Dropdown - Responsive width */}
            {showNotifications && (
              <div className="absolute right-0 top-12 w-[calc(100vw-1.5rem)] max-w-xs sm:w-80 rounded-xl bg-white shadow-xl border border-gray-100 overflow-hidden z-50">
                <div className="border-b border-gray-100 px-4 py-3 bg-gray-50/50">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">
                      {isBrazil ? 'Notificações' : 'Notifications'}
                    </h3>
                    {unreadCount > 0 && (
                      <span className="rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700">
                        {unreadCount} {isBrazil ? 'novas' : 'new'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {unreadNotifications.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <svg className="mx-auto h-8 w-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-500">
                        {isBrazil ? 'Nenhuma notificação nova' : 'No new notifications'}
                      </p>
                    </div>
                  ) : (
                    unreadNotifications.map((notification) => {
                      const localized = getLocalizedNotification(notification);
                      return (
                        <div
                          key={notification.id}
                          onClick={() => markAsRead(notification.id)}
                          className="border-b border-gray-50 px-4 py-3 hover:bg-gray-50/50 transition-colors cursor-pointer"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`mt-0.5 rounded-lg p-1.5 ${notificationStyles[notification.type]?.bg ?? ''}`}>
                              {notification.type === 'warning' && (
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                              )}
                              {notification.type === 'success' && (
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                              {notification.type === 'info' && (
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                              {notification.type === 'error' && (
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{localized.title}</p>
                              <p className="text-xs text-gray-500 mt-0.5 truncate">{localized.message}</p>
                              <p className="text-xs text-gray-400 mt-1">{localized.time}</p>
                            </div>
                            <button
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                              title={isBrazil ? 'Marcar como lida' : 'Mark as read'}
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50">
                  <button
                    onClick={() => {
                      setShowNotifications(false);
                      navigate('/notifications');
                    }}
                    className="w-full text-center text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors"
                  >
                    {isBrazil ? 'Ver todas as notificações' : 'View all notifications'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Market Selector */}
          <div className="relative" ref={marketRef}>
            <button
              onClick={() => {
                setShowMarketSelector(!showMarketSelector);
                setShowNotifications(false);
                setShowProfile(false);
              }}
              className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors border border-gray-200"
              aria-label="Select Market"
            >
              <span className="text-lg">{config.flag}</span>
              <span className="hidden sm:inline">{market === 'US' ? 'US' : 'BR'}</span>
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Market Dropdown */}
            {showMarketSelector && (
              <div className="absolute right-0 top-12 w-64 rounded-xl bg-white shadow-xl border border-gray-100 overflow-hidden z-50">
                <div className="border-b border-gray-100 px-4 py-3 bg-gray-50/50">
                  <h3 className="font-semibold text-gray-900">
                    {isBrazil ? 'Localização do Negócio' : 'Business Location'}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {isBrazil ? 'Selecione onde sua empresa opera' : 'Select where your business operates'}
                  </p>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => {
                      setMarket('US');
                      setShowMarketSelector(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      market === 'US'
                        ? 'bg-teal-50 text-teal-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-2xl">🇺🇸</span>
                    <div className="flex-1">
                      <p className="font-medium">{isBrazil ? 'Estados Unidos' : 'United States'}</p>
                      <p className="text-xs text-gray-500">{isBrazil ? 'USD, EIN, impostos EUA' : 'USD, EIN, US taxes'}</p>
                    </div>
                    {market === 'US' && (
                      <svg className="h-5 w-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setMarket('BR');
                      setShowMarketSelector(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      market === 'BR'
                        ? 'bg-teal-50 text-teal-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-2xl">🇧🇷</span>
                    <div className="flex-1">
                      <p className="font-medium">Brasil</p>
                      <p className="text-xs text-gray-500">BRL, CNPJ, NF-e, ICMS</p>
                    </div>
                    {market === 'BR' && (
                      <svg className="h-5 w-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="border-t border-gray-100 px-4 py-2 bg-gray-50/50">
                  <p className="text-xs text-gray-500">
                    {isBrazil ? 'Altera moeda, campos fiscais e tipos de documentos' : 'Changes currency, tax fields, and document types'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* User Profile with Dropdown */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => {
                setShowProfile(!showProfile);
                setShowNotifications(false);
              }}
              className="hidden items-center gap-3 sm:flex hover:opacity-80 transition-opacity cursor-pointer"
            >
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{user?.name ?? 'User'}</p>
                <p className="text-xs text-gray-400">{user?.email}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 shadow-sm">
                <span className="text-sm font-bold text-white">
                  {user?.name?.charAt(0).toUpperCase() ?? 'U'}
                </span>
              </div>
            </button>

            {/* Profile Dropdown */}
            {showProfile && (
              <div className="absolute right-0 top-12 w-56 rounded-xl bg-white shadow-xl border border-gray-100 overflow-hidden z-50">
                <div className="border-b border-gray-100 px-4 py-3 bg-gray-50/50">
                  <p className="text-sm font-semibold text-gray-900">{user?.name ?? 'User'}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => {
                      setShowProfile(false);
                      navigate('/profile');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {isBrazil ? 'Meu Perfil' : 'My Profile'}
                  </button>
                  <button
                    onClick={() => {
                      setShowProfile(false);
                      navigate('/settings');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {isBrazil ? 'Configurações' : 'Settings'}
                  </button>
                  <button
                    onClick={() => {
                      setShowProfile(false);
                      navigate('/help');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {isBrazil ? 'Ajuda e Suporte' : 'Help & Support'}
                  </button>
                </div>
                <div className="border-t border-gray-100 py-1">
                  <button
                    onClick={() => void handleLogout()}
                    disabled={isLoading}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    {isBrazil ? 'Sair' : 'Sign out'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Logout Button - Hidden on larger screens when profile dropdown exists */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleLogout()}
            disabled={isLoading}
            className="rounded-xl border-gray-200 text-gray-600 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-200 transition-all sm:hidden"
          >
            {isBrazil ? 'Sair' : 'Sign out'}
          </Button>
        </div>
      </div>
    </header>
  );
}
